import type { ColumnType } from 'antd/es/table';
import { CryptoFlag } from 'shared/src/CryptoFlag';
import { CryptoNetworkIcon } from 'shared/src/CryptoNetworkIcon';

import { hasValue } from 'utils/hasValue';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import {
  fallbackTransactionStatusColor,
  transactionStatusColor
} from './transactionStatusColor';
import type { CryptoTransaction } from './useCryptoTransactions';

import { StatusBadge } from '../shared/StatusBadge';

const colSpacing = {
  onHeaderCell: () => ({ style: { padding: '8px 16px' } }),
  onCell: () => ({ style: { padding: '8px 16px' } })
};

const getAsset = (record: CryptoTransaction) =>
  record.type === 'exchange' ? record.from_currency : record.asset;

const getNetwork = (record: CryptoTransaction) =>
  record.type === 'exchange' ? record.from_network : record.network;

const getDepositAmount = (record: CryptoTransaction) =>
  hasValue(record.formatted_amount) && hasValue(record.asset)
    ? `${record.formatted_amount} ${record.asset}`
    : '-';

const getWithdrawalAmount = (record: CryptoTransaction) =>
  hasValue(record.formatted_amount) && hasValue(record.asset)
    ? `${record.formatted_amount} ${record.asset}`
    : '-';

const getExchangeSentAmount = (record: CryptoTransaction) =>
  hasValue(record.formatted_from_amount) && hasValue(record.from_currency)
    ? `-${record.formatted_from_amount} ${record.from_currency}`
    : '-';

const getExchangeReceivedAmount = (record: CryptoTransaction) =>
  hasValue(record.formatted_to_amount) && hasValue(record.to_currency)
    ? `${record.formatted_to_amount} ${record.to_currency}`
    : '-';

const AmountCell = ({ record }: { record: CryptoTransaction }) => {
  if (record.type === 'deposit') {
    return <p className="font-medium">{getDepositAmount(record)}</p>;
  }

  if (record.type === 'withdrawal') {
    return <p className="font-medium">{getWithdrawalAmount(record)}</p>;
  }

  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="text-xs text-content-secondary">
        {getExchangeSentAmount(record)}
      </span>
      <span>{getExchangeReceivedAmount(record)}</span>
    </div>
  );
};

export const transactionColumns: ColumnType<CryptoTransaction>[] = [
  {
    ...colSpacing,
    key: 'asset',
    width: 5,
    render: (_, record) => (
      <div className="relative inline-flex">
        <CryptoFlag cryptoCode={getAsset(record) ?? ''} className="size-6" />
        <CryptoNetworkIcon
          network={getNetwork(record) ?? ''}
          className="absolute -bottom-0.5 -right-0.5 size-3"
        />
      </div>
    )
  },
  {
    ...colSpacing,
    title: 'Type',
    dataIndex: 'display_type',
    key: 'display_type',
    align: 'center',
    render: (displayType: string | null | undefined) => (
      <p className="font-medium">{displayType ?? '-'}</p>
    )
  },
  {
    ...colSpacing,
    title: 'Amount',
    align: 'right',
    render: (_, record) => <AmountCell record={record} />
  },
  {
    ...colSpacing,
    title: 'Date',
    dataIndex: 'display_at',
    key: 'display_at',
    align: 'right',
    render: (value: string | null | undefined) =>
      hasValue(value) ? <FormattedDateTime value={value} format="cet" /> : '-'
  },
  {
    ...colSpacing,
    title: 'Status',
    dataIndex: 'status',
    align: 'right',
    key: 'status',
    render: (status: string | null | undefined) =>
      hasValue(status) ? (
        <StatusBadge
          color={
            transactionStatusColor[status] ?? fallbackTransactionStatusColor
          }
          statusText={status}
        />
      ) : (
        '-'
      )
  }
];
